import React, { useRef, useCallback, useEffect, useState, useMemo } from 'react';
import { useClickableTooltip } from '../hooks/useClickableTooltip';

interface BaseTooltipProps {
  triggerRef: React.RefObject<HTMLElement>;
  children: React.ReactNode | ((closeTooltip: () => void) => React.ReactNode);
  className?: string;
}

const BaseTooltip: React.FC<BaseTooltipProps> = ({ triggerRef, children, className = '' }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { isVisible, closeTooltip } = useClickableTooltip({
    triggerRef,
    tooltipRef
  });
  const [position, setPosition] = useState({ bottom: 0, left: 0 });

  // Cache for performance optimization
  const scrollableContainersRef = useRef<HTMLElement[]>([]);
  const lastCalculationRef = useRef<{ position: { bottom: number; left: number }; timestamp: number } | null>(null);

  // Memoized function to find scrollable containers (only recalculates when trigger changes)
  const findScrollableContainers = useMemo(() => {
    return (element: HTMLElement): HTMLElement[] => {
      const containers: HTMLElement[] = [];
      let current = element.parentElement;

      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        const hasScroll =
          style.overflow === 'auto' ||
          style.overflow === 'scroll' ||
          style.overflowX === 'auto' ||
          style.overflowX === 'scroll' ||
          style.overflowY === 'auto' ||
          style.overflowY === 'scroll';

        if (hasScroll || current.scrollWidth > current.clientWidth || current.scrollHeight > current.clientHeight) {
          containers.push(current);
        }
        current = current.parentElement;
      }

      return containers;
    };
  }, []);

  // Calculate position with caching for performance
  const calculatePosition = useCallback(() => {
    const now = performance.now();

    // Cache result for 8ms to avoid excessive calculations during rapid events
    if (lastCalculationRef.current && now - lastCalculationRef.current.timestamp < 8) {
      return lastCalculationRef.current.position;
    }

    const triggerElement = triggerRef.current;
    if (!triggerElement) {
      return { bottom: 0, left: 0 };
    }

    const rect = triggerElement.getBoundingClientRect();
    const newPosition = {
      bottom: window.innerHeight - rect.top, // Position above the trigger
      left: rect.left + rect.width / 2 // Center horizontally on trigger
    };

    // Cache the result
    lastCalculationRef.current = {
      position: newPosition,
      timestamp: now
    };

    return newPosition;
  }, [triggerRef]);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      setPosition(calculatePosition());
    }
  }, [isVisible, calculatePosition]);

  // Update position on scroll and zoom events with performance optimizations
  useEffect(() => {
    if (!isVisible) {
      return;
    }

    const triggerElement = triggerRef.current;
    if (!triggerElement) {
      return;
    }

    // Use cached containers or find them once
    if (scrollableContainersRef.current.length === 0) {
      scrollableContainersRef.current = findScrollableContainers(triggerElement);
    }

    // Use requestAnimationFrame for smooth updates and better performance
    let rafId: number | null = null;
    let isUpdateScheduled = false;

    const updatePosition = () => {
      if (isUpdateScheduled) {
        return;
      }

      isUpdateScheduled = true;
      rafId = requestAnimationFrame(() => {
        setPosition(calculatePosition());
        isUpdateScheduled = false;
        rafId = null;
      });
    };

    // Use a single optimized wheel handler
    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey) {
        // This is likely a zoom operation - update immediately for better UX
        updatePosition();
      }
    };

    // Add listeners with optimized approach
    const containers = scrollableContainersRef.current;

    containers.forEach(container => {
      container.addEventListener('scroll', updatePosition, { passive: true });
      container.addEventListener('wheel', handleWheel, { passive: true });
    });

    // Global listeners for window events
    window.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition, { passive: true });

    return () => {
      // Cleanup
      if (rafId) {
        cancelAnimationFrame(rafId);
      }

      containers.forEach(container => {
        container.removeEventListener('scroll', updatePosition);
        container.removeEventListener('wheel', handleWheel);
      });

      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isVisible, calculatePosition, triggerRef, findScrollableContainers]);

  // Reset caches when tooltip closes for memory efficiency
  useEffect(() => {
    if (!isVisible) {
      scrollableContainersRef.current = [];
      lastCalculationRef.current = null;
    }
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      ref={tooltipRef}
      data-tooltip-overlay
      className={`fixed z-[99999] w-64 cursor-default rounded-xl border border-gray-200 bg-white shadow-lg ${className}`}
      style={{
        bottom: `${position.bottom}px`,
        left: `${position.left}px`,
        transform: 'translateX(-50%)'
      }}
      onClick={e => e.stopPropagation()}
      onMouseDown={e => e.stopPropagation()}
      // onMouseUp={e => e.stopPropagation()}
    >
      {/* Arrow pointing down */}
      <div className="absolute -bottom-1 left-1/2 size-1.5 -translate-x-1/2 rotate-45 border-b border-r border-gray-200 bg-white"></div>

      <div className="p-3">{typeof children === 'function' ? children(closeTooltip) : children}</div>
    </div>
  );
};

export default BaseTooltip;
